import { RangeFilterValue } from "./RangeListTypes";
import { RangeHeaderData } from "./RangeHeader";

/**
 * Utility functions for serializing and deserializing RangeList state to/from URL
 */

/**
 * Convert headerData filterAnd/filterOr to RangeFilterValue array
 */
export function headerDataToFilterValues(headerData: RangeHeaderData): RangeFilterValue[] {
   const result: RangeFilterValue[] = [];

   for (const key of Object.keys(headerData.filterAnd || {})) {
      const selected = headerData.filterAnd[key];
      if (selected?.length > 0) {
         result.push({ filterId: key, values: selected, requireAll: true });
      }
   }

   for (const key of Object.keys(headerData.filterOr || {})) {
      const selected = headerData.filterOr[key];
      if (selected?.length > 0) {
         result.push({ filterId: key, values: selected, requireAll: false });
      }
   }

   return result;
}

/**
 * Serialize filters to URL-friendly string
 * Format: filterId:value1,value2,value3|requireAll;filterId2:value1,value2|requireAll
 */
export function serializeFilters(filters: RangeFilterValue[]): string {
   if (!filters || filters.length === 0) return '';

   return filters.map(filter => {
      const requireAllParam = filter.requireAll ? '1' : '0';
      return `${filter.filterId}:${filter.values.join(',')}|${requireAllParam}`;
   }).join(';');
}

/**
 * Deserialize filters from URL string
 */
export function deserializeFilters(filterStr: string): RangeFilterValue[] {
   if (!filterStr) return [];

   return filterStr.split(';').map(filterPart => {
      const [filterWithValues, requireAllStr] = filterPart.split('|');
      const [filterId, valuesStr] = filterWithValues.split(':');
      const values = valuesStr ? valuesStr.split(',') : [];

      return {
         filterId,
         values,
         requireAll: requireAllStr === '1'
      };
   });
}

/**
 * Helper function to update search params without losing existing params
 */
export function updateSearchParams(
   searchParams: URLSearchParams,
   updates: Record<string, string | null>
): URLSearchParams {
   const newParams = new URLSearchParams(searchParams);

   for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '') {
         newParams.delete(key);
      } else {
         newParams.set(key, value);
      }
   }

   return newParams;
}

/**
 * Get URL parameters from RangeList header state
 */
export function getRangeListURLParams(
   headerData: RangeHeaderData,
   tab?: string | null
): Record<string, string | null> {
   const filters = headerDataToFilterValues(headerData);

   return {
      search: headerData.search || null,
      filters: filters.length > 0 ? serializeFilters(filters) : null,
      sort: headerData.sort || null,
      tab: tab || null
   };
}

/**
 * Convert RangeFilterValue array to filterAnd and filterOr objects
 */
export function filtersToFilterObjects(filters: RangeFilterValue[]): {
   filterAnd: { [id: string]: string[] },
   filterOr: { [id: string]: string[] }
} {
   const filterAnd: { [id: string]: string[] } = {};
   const filterOr: { [id: string]: string[] } = {};

   for (const filter of filters) {
      if (filter.requireAll) {
         filterAnd[filter.filterId] = filter.values;
      } else {
         filterOr[filter.filterId] = filter.values;
      }
   }

   return { filterAnd, filterOr };
}

/**
 * Parse URL search params and return RangeHeaderData
 */
export function parseURLToHeaderData(searchParams: URLSearchParams): RangeHeaderData {
   const filters = deserializeFilters(searchParams.get('filters') || '');
   const { filterAnd, filterOr } = filtersToFilterObjects(filters);

   return {
      search: searchParams.get('search') || '',
      filterAnd,
      filterOr,
      sort: searchParams.get('sort') || undefined
   };
}
